//CameraModal.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal, CircularProgress, Box, Typography } from "@mui/material";
import * as faceapi from "face-api.js";
import "../../style/CameraModal.css";
import CustomSnackbar from "../common/CustomSnackbar";
import { candidateApi } from "../../api/candidateApi";
import CustomButton from "../common/CustomButton";

// ── Detection options ──
// Monitor: small + fast for live indicator (runs every 600ms)
const MONITOR_OPTIONS = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.3 });
// Capture: larger + stricter for final validation
const CAPTURE_OPTIONS = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });

/**
 * Validates that the detected face has eyes, nose, and mouth
 * in their correct relative positions (i.e., face is not covered).
 */
const isFaceProperlyVisible = (result) => {
  if (!result || !result.landmarks) return false;

  const pts = result.landmarks.positions; // 68 points

  const avg = (slice) => ({
    x: slice.reduce((s, p) => s + p.x, 0) / slice.length,
    y: slice.reduce((s, p) => s + p.y, 0) / slice.length,
  });

  const leftEye = avg(pts.slice(36, 42));  // left eye
  const rightEye = avg(pts.slice(42, 48));  // right eye
  const noseTip = pts[33];                 // nose tip
  const mouth = avg(pts.slice(48, 68));  // mouth

  const eyesMidY = (leftEye.y + rightEye.y) / 2;
  const faceWidth = result.detection.box.width;
  const faceHeight = result.detection.box.height;

  // 1. Eyes must be horizontally separated (not collapsed / distorted)
  const eyeDist = Math.abs(rightEye.x - leftEye.x);
  const eyesSeparated = eyeDist > faceWidth * 0.2;

  // 2. Mouth must be clearly below eye level
  const mouthBelowEyes = mouth.y > eyesMidY + faceHeight * 0.1;

  // 3. Nose tip must sit between eyes and mouth vertically
  const noseInRange = noseTip.y > eyesMidY && noseTip.y < mouth.y;

  return eyesSeparated && mouthBelowEyes && noseInRange;
};

const CameraModal = ({ open, onClose, payload }) => {
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [detectingFace, setDetectingFace] = useState(false);
  const [facePresent, setFacePresent] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const monitorRef = useRef(null);
  const isFacePresentRef = useRef(false);
  const modelsLoadedRef = useRef(false);

  // ── Load both models on mount ──
  useEffect(() => {
    Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri("/models"),
      faceapi.nets.faceLandmark68Net.loadFromUri("/models"),
    ])
      .then(() => {
        setModelsLoaded(true);
        modelsLoadedRef.current = true;
        // console.log("✅ face-api models ready (detector + landmarks)");
      })
      .catch((err) => console.error("❌ face-api load error:", err));
  }, []);

  // ── Continuous face monitoring (lightweight — no landmarks) ──
  const startMonitoring = useCallback(() => {
    if (monitorRef.current) return;

    monitorRef.current = setInterval(async () => {
      if (!modelsLoadedRef.current) return;
      const video = videoRef.current;
      if (!video || video.readyState < 2 || !video.videoWidth) return;

      try {
        const result = await faceapi.detectSingleFace(video, MONITOR_OPTIONS);
        const found = !!result;
        isFacePresentRef.current = found;
        setFacePresent(found);
      } catch {
        // transient errors — ignore
      }
    }, 300);
  }, []);

  const stopMonitoring = useCallback(() => {
    clearInterval(monitorRef.current);
    monitorRef.current = null;
    isFacePresentRef.current = false;
    setFacePresent(false);
  }, []);

  // ── Camera helpers ──
  const startCamera = useCallback(async () => {
    try {
      setLoading(true);
      setCameraReady(false);
      setPhoto(null);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      });

      streamRef.current = mediaStream;
      streamRef.current = mediaStream;
      setStream(mediaStream);

      const video = videoRef.current;
      if (video) {
        video.srcObject = mediaStream;
        video.onloadedmetadata = () => {
          video.play().then(() => {
            setCameraReady(true);
            setLoading(false);
            startMonitoring();
          }).catch(() => setLoading(false));
        };
      }
    } catch {
      setSnackbar({ open: true, message: "Cannot access camera", severity: "error" });
      setLoading(false);
    }
  }, [startMonitoring]);

  const stopCamera = useCallback(() => {
    stopMonitoring();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStream(null);
    setCameraReady(false);
  }, [stopMonitoring]);

  // ── Countdown ──
  const capturePhoto = useCallback(() => {
    if (!cameraReady) return;

    // Quick pre-check using continuous monitor result
    if (modelsLoadedRef.current && !isFacePresentRef.current) {
      setSnackbar({
        open: true,
        message: "No face detected. Please position your face clearly in the circle.",
        severity: "error",
      });
      return;
    }

    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          validateAndSnapshot(); // runs AFTER countdown ends
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraReady]);

  // ── Final capture: validates face + landmarks, then snapshots ──
  const validateAndSnapshot = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth) {
      setSnackbar({ open: true, message: "Camera not ready. Please try again.", severity: "warning" });
      return;
    }

    // Show verifying overlay
    setDetectingFace(true);

    try {
      if (modelsLoadedRef.current) {
        // Run detection WITH landmarks for strict face visibility check
        const result = await faceapi
          .detectSingleFace(video, CAPTURE_OPTIONS)
          .withFaceLandmarks(); // uses faceLandmark68Net

        if (!result) {
          setSnackbar({
            open: true,
            message: "No face detected. Please look directly at the camera.",
            severity: "error",
          });
          return;
        }

        if (!isFaceProperlyVisible(result)) {
          setSnackbar({
            open: true,
            message: "Your face appears to be partially covered. Please ensure your full face is clearly visible.",
            severity: "error",
          });
          return;
        }
      }

      // ── Face is confirmed visible — take the snapshot ──
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");

      // Mirror for natural selfie look
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = canvas.toDataURL("image/jpeg", 0.92);
      stopCamera();
      setPhoto(imageData);

    } catch (err) {
      console.error("Capture validation error:", err);
      // On unexpected error, allow through (graceful degradation)
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      stopCamera();
      setPhoto(canvas.toDataURL("image/jpeg", 0.92));
    } finally {
      setDetectingFace(false);
    }
  }, [stopCamera]);

  // ── Submit ──
  // ── Submit ──
  const handleSubmit = async () => {
    if (!photo || !payload?.candidate_id) {
      setSnackbar({ open: true, message: "Something went wrong. Please restart the interview!", severity: "warning" });
      setSnackbar({ open: true, message: "Something went wrong. Please restart the interview!", severity: "warning" });
      return;
    }
    try {
      setLoading(true);
      const [header, data] = photo.split(",");
      const mime = header.match(/:(.*?);/)[1];
      const bstr = atob(data);
      const u8 = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
      const file = new File([u8], `candidate_${payload.candidate_id}.jpg`, { type: mime });

      const formData = new FormData();
      formData.append("image", file);

      const response = await candidateApi.uploadImage(formData);
      const { status, message } = response || {};

      if (status === 0) { setSnackbar({ open: true, message: "Network error. Please check your connection.", severity: "warning" }); return; }
      if (status >= 500) { setSnackbar({ open: true, message: message || "Server error. Please try again.", severity: "error" }); return; }
      if (status >= 400) { setSnackbar({ open: true, message: message || "Invalid image upload.", severity: "error" }); return; }
      if (status === 200 || status === 201) {
        setSnackbar({ open: true, message: message || "Photo uploaded successfully!", severity: "success" });
        onClose();
        return;
      }
      setSnackbar({ open: true, message: "Unexpected server response.", severity: "error" });
    } catch {
      setSnackbar({ open: true, message: "Something went wrong while uploading.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    setPhoto(null);
    setCountdown(0);
    onClose(null);
  };

  // ── Lifecycle ──
  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
      setPhoto(null);
      setCountdown(0);
    }
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Face circle indicator color ──
  const circleColor = !modelsLoaded || !cameraReady
    ? "rgba(255,255,255,0.7)"
    : facePresent
      ? "rgba(34,197,94,0.95)"
      : "rgba(239,68,68,0.7)";

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") handleClose();
      }}
    >
      <Box className="camera-modal" sx={{ position: "relative" }}>
        <Typography variant="h6" fontWeight={600}>
          Photo Verification
        </Typography>

        {/* Verifying / uploading overlay */}
        {(detectingFace || (loading && photo)) && (
          <Box sx={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.82)",
            zIndex: 10, borderRadius: 2, gap: 1.5,
          }}>
            <CircularProgress />
            {detectingFace && (
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Verifying face visibility…
              </Typography>
            )}
          </Box>
        )}

        {!photo ? (
          <>
            <div className="video-wrapper">
              <video ref={videoRef} autoPlay muted playsInline className="video-feed" />

              {countdown > 0 && <div className="countdown">{countdown}</div>}

              {/* Circle turns green/red based on live detection */}
              <div className="face-guide">
                <div
                  className="face-circle"
                  style={{
                    borderColor: circleColor,
                    borderStyle: "solid",
                    transition: "border-color 0.4s ease",
                  }}
                >
                  <span style={{ color: "#fff", fontSize: "11px" }}>
                    {cameraReady && modelsLoaded
                      ? facePresent ? "✓ Face ready" : "No face detected"
                      : "Your face should be here"}
                  </span>
                </div>
              </div>
            </div>

            {loading && !photo && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            <CustomButton
              variant="contained"
              onClick={capturePhoto}
              disabled={!cameraReady || countdown > 0 || detectingFace}
              loading={loading && !photo}
              fullWidth
              sx={{ mt: 2 }}
            >
              Capture
            </CustomButton>
          </>
        ) : (
          <>
            <img src={photo} alt="Captured" className="photo-preview" />
            <Box sx={{ display: "flex", gap: 2, mt: 2, justifyContent: "flex-end" }}>
              <CustomButton
                variant="outlined"
                onClick={() => { setPhoto(null); startCamera(); }}
              >
                Retake
              </CustomButton>
              <CustomButton variant="contained" onClick={handleSubmit} loading={loading}>
                Submit
              </CustomButton>
            </Box>
          </>
        )}

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          type={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          autoHideDuration={6000}
        />
      </Box>
    </Modal>
  );
};

export default CameraModal;